// Where the Chain graph puts things: a vertical line on the left (lanes and
// stations), one row per step on the right. Rows follow the order of the steps
// (parents first); lanes are chosen from the top down. Kept apart from the
// drawing (chain-graph.tsx) as pure functions so the shapes can be tested.
import { type CaseStep, orderSteps } from "@trameria/core/cases-shared";

export type LaneState = "done" | "now" | "later";

export interface LaneRow {
  step: CaseStep;
  /** The row's number from the top (0). */
  index: number;
  lane: number;
  /** Parents (in creation order) and children (in row order) shown in the graph. */
  parents: string[];
  children: string[];
  /** A fork or a join (two or more children or parents). */
  hub: boolean;
  state: LaneState;
  /** The color of the station and of the line down its lane (one of `LINE_COLORS`), per line, not per lane. */
  color: number;
}

/** How many line colors there are (`lineColor` in chain-graph.tsx). */
export const LINE_COLORS = 7;

/** Beyond this many lanes, closed branches are laid over their parent's lane. */
const MAX_LANES = 9;

const stateOf = (s: CaseStep): LaneState =>
  s.status !== "open" ? "done" : s.current ? "now" : "later";

/**
 * Rows and lanes.
 *
 * - A child keeps its parent's lane when it is the parent's first child and
 *   the lane is still free (no line going down from the parent to a join);
 *   other children take the leftmost lane free from the parent's row down.
 * - A join (two or more parents) takes the leftmost of its parents' lanes; a
 *   parent's lane stays taken down to the join's row (its line goes there).
 * - A lane is taken down to the lowest row of the line running in it, and is
 *   free again after it (a later branch reuses it).
 * - A step without parents takes the leftmost lane free from its row down,
 *   even right under a line that ended, but not a lane whose line still goes
 *   on to a child below.
 * - Colors go by line: each new line (a step without parents, a branch leaving
 *   its parent's lane) gets the next color; a child going straight down and a
 *   join keep the color of that lane's parent. A lane used again gets a color
 *   other than the station above it, so the new start does not read as the
 *   same line broken off.
 */
export function laneLayout(steps: readonly CaseStep[]): {
  rows: LaneRow[];
  lanes: number;
} {
  const first = place(steps, false);
  if (first.lanes <= MAX_LANES) return first;
  // Lay closed branches over the parent's lane; if still too many, the lanes
  // stay as they are (the spacing is already tight).
  return place(steps, true);
}

function place(
  steps: readonly CaseStep[],
  foldDone: boolean,
): { rows: LaneRow[]; lanes: number } {
  const ordered = orderSteps(steps);
  const index = new Map(ordered.map((s, i) => [s.id, i]));
  const rows: LaneRow[] = ordered.map((step, i) => ({
    step,
    index: i,
    lane: 0,
    parents: step.parentIds.filter((p) => p !== step.id && index.has(p)),
    children: [],
    hub: false,
    state: stateOf(step),
    color: 0,
  }));
  const at = (id: string) => index.get(id) ?? 0;
  for (const r of rows)
    for (const p of r.parents) rows[at(p)].children.push(r.step.id);
  for (const r of rows) {
    r.children.sort((a, b) => at(a) - at(b));
    r.hub = r.children.length > 1 || r.parents.length > 1;
  }
  const rowOf = (id: string) => rows[at(id)];
  // Per lane, the lowest row it is taken to (-1: free).
  const busy: number[] = [];
  // The leftmost lane free from row `from` down. A line ending on `from` (into
  // a join at 45 degrees) does not collide: forks leave 4px under the station.
  const freeFrom = (from: number) => {
    let l = 0;
    while ((busy[l] ?? -1) > from) l += 1;
    return l;
  };
  // Per lane, the row and color of the last station put there.
  const last: number[] = [];
  const lastColor: number[] = [];
  const rootLane = (from: number) => {
    for (let l = 0; ; l += 1) {
      if ((busy[l] ?? -1) > from) continue;
      const k = last[l];
      if (k === undefined || rows[k].children.every((id) => at(id) < from))
        return l;
    }
  };
  // The next color, skipping the color of the station above in the lane and
  // the colors of the parents it leaves. Colors 0 and 5 are both blue, so
  // under one another they count as the same.
  let next = 0;
  const hue = (c: number) => (c === 5 ? 0 : c);
  const newColor = (above: number | undefined, parents: number[]) => {
    const blocked = (c: number) =>
      parents.includes(c) || (above !== undefined && hue(c) === hue(above));
    let c = next % LINE_COLORS;
    for (let i = 0; i < LINE_COLORS && blocked(c); i += 1)
      c = (c + 1) % LINE_COLORS;
    next = c + 1;
    return c;
  };
  for (const r of rows) {
    const ps = r.parents.map(rowOf);
    if (ps.length === 0) r.lane = rootLane(r.index);
    else if (ps.length > 1) r.lane = Math.min(...ps.map((p) => p.lane));
    else {
      const p = ps[0];
      const fold =
        foldDone && r.state === "done" && !r.hub && p.state === "done";
      const inherit =
        p.children[0] === r.step.id && (busy[p.lane] ?? -1) <= p.index;
      r.lane = fold || inherit ? p.lane : freeFrom(p.index);
    }
    // Going straight down a parent's lane (a join: the parent in the join's
    // lane) keeps that parent's line color.
    const along = ps
      .filter((p) => p.lane === r.lane)
      .sort((a, b) => b.index - a.index)[0];
    r.color = along
      ? along.color
      : newColor(
          lastColor[r.lane],
          ps.map((p) => p.color),
        );
    lastColor[r.lane] = r.color;
    // Take this row, and the lane down to where a line from here enters a join.
    let until = r.index;
    for (const k of r.children) {
      const c = rowOf(k);
      if (c.parents.length > 1) until = Math.max(until, c.index);
    }
    busy[r.lane] = Math.max(busy[r.lane] ?? -1, until);
    last[r.lane] = r.index;
  }
  const lanes =
    rows.length === 0 ? 0 : Math.max(...rows.map((r) => r.lane)) + 1;
  return { rows, lanes };
}

/** The graph's measures. */
export interface Geo {
  rowH: number;
  /** Room above and below. */
  pad: number;
  x0: number;
  /** Between lanes. */
  gap: number;
  lineWidth: number;
  /** Closed and later stations: the ring's radius and width, the closed dot's radius. */
  ringR: number;
  ringW: number;
  dotR: number;
  /** The current station: the tinted halo, the ring that spreads (up to 1.35 times), the core. */
  haloR: number;
  pulseR: number;
  pulseW: number;
  coreR: number;
  coreW: number;
}

/**
 * The measures: always the tight ones (10px between lanes), however many lanes
 * there are, so a Case looks the same as it grows. A current station (its
 * spreading ring too) never covers the line of the next lane.
 */
export function geometry(): Geo {
  return {
    rowH: 48,
    pad: 10,
    x0: 24,
    gap: 10,
    lineWidth: 2.25,
    ringR: 4.5,
    ringW: 1.5,
    dotR: 2,
    haloR: 7.5,
    pulseR: 5.5,
    pulseW: 1.5,
    coreR: 4.75,
    coreW: 2.25,
  };
}

export const laneX = (lane: number, g: Geo) => g.x0 + lane * g.gap;
export const rowY = (index: number, g: Geo) =>
  g.pad + index * g.rowH + g.rowH / 2;
export const canvasHeight = (rows: number, g: Geo) => g.pad * 2 + g.rowH * rows;

/** A line's color: the station on the side whose lane it runs down (the child for a fork, the parent for a join). */
export function edgeColor(p: LaneRow, c: LaneRow): number {
  return c.parents.length > 1 ? p.color : c.color;
}

/**
 * The bends of a parent → child line, from the top. In one lane it is
 * straight. Into a join it runs down the parent's lane and turns in at 45
 * degrees just before the child. A fork leaves 4px under the parent, turns out
 * at 45 degrees to the child's lane and runs down. When the rows are too close
 * for 45 degrees, it goes straight from 4px under the parent to 4px over the
 * child (never above the parent nor past the child).
 */
function edgePoints(p: LaneRow, c: LaneRow, g: Geo): [number, number][] {
  const xp = laneX(p.lane, g);
  const yp = rowY(p.index, g);
  const xc = laneX(c.lane, g);
  const yc = rowY(c.index, g);
  const ad = Math.abs(xc - xp);
  if (ad < 0.5)
    return [
      [xp, yp],
      [xc, yc],
    ];
  if (c.parents.length > 1) {
    const bend = Math.max(yc - ad - 4, yp + 4);
    return [
      [xp, yp],
      [xp, bend],
      [xc, yc - 4],
      [xc, yc],
    ];
  }
  return [
    [xp, yp],
    [xp, yp + 4],
    [xc, Math.min(yp + 4 + ad, yc - 4)],
    [xc, yc],
  ];
}

/** A parent → child line as an SVG path (shape: {@link edgePoints}). */
export function edgePath(p: LaneRow, c: LaneRow, g: Geo): string {
  const pts = edgePoints(p, c, g);
  const [x0, y0] = pts[0];
  let d = `M${x0} ${y0}`;
  for (let i = 1; i < pts.length; i += 1) {
    const [x, y] = pts[i];
    d += x === pts[i - 1][0] ? ` V${y}` : ` L${x} ${y}`;
  }
  return d;
}

/** A station's outer radius (its right edge, for where titles start). */
export function stationRadius(r: LaneRow, g: Geo): number {
  return r.state === "now" ? g.haloR : g.ringR + g.ringW / 2;
}

/**
 * A vertical range and the right edge of what fills it (a line or a station).
 * A slanted line has a different right edge at its top and bottom (straight
 * in between).
 */
export interface Span {
  y1: number;
  y2: number;
  /** The right edge at y1. */
  r1: number;
  /** The right edge at y2. */
  r2: number;
  /** A station's range (the whole row): tells which rows' titles are seen. */
  row?: true;
}

/** Half a title's height: a row seen less than this from its middle has no title seen. */
const TITLE_HALF = 12;

/** The ranges of every line piece and station, made once per drawing. */
export function titleSpans(rows: readonly LaneRow[], g: Geo): Span[] {
  const byId = new Map(rows.map((r) => [r.step.id, r]));
  const half = g.lineWidth / 2;
  const out: Span[] = [];
  for (const c of rows)
    for (const id of c.parents) {
      const p = byId.get(id);
      if (!p) continue;
      const pts = edgePoints(p, c, g);
      for (let i = 1; i < pts.length; i += 1) {
        const [xa, ya] = pts[i - 1];
        const [xb, yb] = pts[i];
        if (yb <= ya) continue;
        out.push({ y1: ya, y2: yb, r1: xa + half, r2: xb + half });
      }
    }
  for (const r of rows) {
    const y = rowY(r.index, g);
    const right = laneX(r.lane, g) + stationRadius(r, g);
    out.push({
      y1: y - g.rowH / 2,
      y2: y + g.rowH / 2,
      r1: right,
      r2: right,
      row: true,
    });
  }
  return out;
}

/**
 * Where titles start within `top`..`bottom`: 14px right of the rightmost line
 * or station there (at least 46px). Only what crosses the rows whose titles
 * are seen counts — not a branch outside the range, not a row barely left at
 * an edge (its title is not seen) nor the line leaving it; a slanted line
 * counts only for its part inside.
 */
export function titleLeft(
  spans: readonly Span[],
  top: number,
  bottom: number,
): number {
  let a = top + 6;
  let b = bottom - 6;
  let first = Number.POSITIVE_INFINITY;
  let last = Number.NEGATIVE_INFINITY;
  for (const s of spans) {
    if (!s.row) continue;
    const mid = (s.y1 + s.y2) / 2;
    if (mid + TITLE_HALF <= a || mid - TITLE_HALF >= b) continue;
    first = Math.min(first, mid - TITLE_HALF);
    last = Math.max(last, mid + TITLE_HALF);
  }
  if (first < last) {
    a = Math.max(a, first);
    b = Math.min(b, last);
  }
  let right = 0;
  for (const s of spans) {
    if (s.y2 <= a || s.y1 >= b) continue;
    const xAt = (y: number) =>
      s.r1 + ((s.r2 - s.r1) * (y - s.y1)) / (s.y2 - s.y1);
    right = Math.max(right, xAt(Math.max(s.y1, a)), xAt(Math.min(s.y2, b)));
  }
  return Math.max(Math.round(right + 14), 46);
}
